import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../styles/roman-roads-explorer.css";

const BRITANNIA_BOUNDS = [[49.5, -8.8], [59.1, 2.2]];
const DEVA = [53.191, -2.891];

const TYPE_LABELS = {
  "Main Road": "Main roads",
  "Secondary Road": "Secondary roads",
  River: "Rivers",
  "Sea Lane": "Sea lanes",
};

const CERTAINTY_LABELS = {
  Certain: "Certain",
  Conjectured: "Conjectured",
  Hypothetical: "Hypothetical",
};

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function formatYear(year) {
  if (year === null || year === undefined || Number.isNaN(Number(year))) return null;
  const y = Number(year);
  if (y === 0) return "1 BCE/CE";
  return y < 0 ? `${Math.abs(y)} BCE` : `${y} CE`;
}

function formatDateRange(props) {
  const lower = formatYear(props.lowerDate);
  const upper = formatYear(props.upperDate);
  if (!lower && !upper) return null;
  if (lower && upper) return `${lower} – ${upper}`;
  return lower || upper;
}

function routeColour(type) {
  if (type === "Main Road") return "#8f3f2f";
  if (type === "Secondary Road") return "#ba7a45";
  if (type === "River") return "#467d91";
  if (type === "Sea Lane") return "#61788f";
  return "#655f56";
}

function routeStyle(feature) {
  const p = feature.properties || {};
  const certainty = p.segmentCertainty || "Conjectured";
  return {
    color: routeColour(p.type),
    weight: p.type === "Main Road" ? 4 : p.type === "Secondary Road" ? 2.5 : 2,
    opacity: certainty === "Hypothetical" ? 0.58 : 0.88,
    dashArray: certainty === "Hypothetical" ? "3 7" : null,
    lineCap: "round",
  };
}

function makeRoutePopup(feature, placesById) {
  const p = feature.properties || {};
  const nearby = (p.nearbyPlaceIds || [])
    .map((id) => placesById.get(String(id))?.properties?.name)
    .filter(Boolean);
  const date = formatDateRange(p);
  const length = Number(p._lengthInKm);
  const rows = [
    ["Class", p.type],
    ["Evidence", p.segmentCertainty],
    ["Length", Number.isFinite(length) ? `${length.toFixed(1)} km` : null],
    ["Dating", date],
    ["Construction", p.constructionPeriod],
    ["Ancient itinerary", p.itinerary],
    ["Nearby places", nearby.length ? nearby.join(", ") : null],
    ["Bibliography", p.bibliography],
    ["Contributor", p.author],
  ].filter(([, value]) => value);

  return `
    <article class="rr-popup">
      <div class="rr-popup-kicker">Itiner-e route segment</div>
      <h3>${escapeHtml(p.name || "Unnamed route")}</h3>
      <dl>
        ${rows.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join("")}
      </dl>
      <div class="rr-popup-note">Roman Storyworld displays the source classification rather than treating a reconstructed route as established fact.</div>
    </article>`;
}

export default function RomanRoadsExplorer() {
  const mapNode = useRef(null);
  const mapRef = useRef(null);
  const routeLayerRef = useRef(null);
  const placeLayerRef = useRef(null);
  const [routes, setRoutes] = useState(null);
  const [places, setPlaces] = useState(null);
  const [loadingError, setLoadingError] = useState("");
  const [query, setQuery] = useState("");
  const [showPlaces, setShowPlaces] = useState(true);
  const [types, setTypes] = useState({
    "Main Road": true,
    "Secondary Road": true,
    River: false,
    "Sea Lane": false,
  });
  const [certainties, setCertainties] = useState({
    Certain: true,
    Conjectured: true,
    Hypothetical: true,
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch("/data/itiner-e-britannia.geojson").then((r) => {
        if (!r.ok) throw new Error(`Road data returned ${r.status}`);
        return r.json();
      }),
      fetch("/data/itiner-e-britannia-places.geojson").then((r) => {
        if (!r.ok) throw new Error(`Place data returned ${r.status}`);
        return r.json();
      }),
    ])
      .then(([routeData, placeData]) => {
        if (cancelled) return;
        setRoutes(routeData);
        setPlaces(placeData);
      })
      .catch((error) => {
        if (!cancelled) setLoadingError(error.message || "Could not load the map data.");
      });
    return () => { cancelled = true; };
  }, []);

  const placesById = useMemo(() => {
    const map = new Map();
    for (const place of places?.features || []) map.set(String(place.id), place);
    return map;
  }, [places]);

  const certaintyCounts = useMemo(() => {
    const counts = { Certain: 0, Conjectured: 0, Hypothetical: 0 };
    for (const feature of routes?.features || []) {
      const certainty = feature.properties?.segmentCertainty || "Conjectured";
      if (Object.prototype.hasOwnProperty.call(counts, certainty)) counts[certainty] += 1;
    }
    return counts;
  }, [routes]);

  const visibleRoutes = useMemo(() => {
    if (!routes) return [];
    const needle = query.trim().toLowerCase();
    return routes.features.filter((feature) => {
      const p = feature.properties || {};
      if (!types[p.type]) return false;
      if (!certainties[p.segmentCertainty || "Conjectured"]) return false;
      if (!needle) return true;
      const haystack = [p.name, p.type, p.itinerary, p.bibliography, p.author]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [routes, query, types, certainties]);

  useEffect(() => {
    if (!mapNode.current || mapRef.current) return;
    const map = L.map(mapNode.current, { zoomControl: false, minZoom: 5, maxZoom: 15 });
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    map.fitBounds(BRITANNIA_BOUNDS, { padding: [12, 12] });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routes || !places) return;

    if (routeLayerRef.current) routeLayerRef.current.remove();
    routeLayerRef.current = L.geoJSON(
      { type: "FeatureCollection", features: visibleRoutes },
      {
        style: routeStyle,
        onEachFeature: (feature, layer) => {
          layer.bindPopup(makeRoutePopup(feature, placesById), { maxWidth: 390 });
        },
      }
    ).addTo(map);

    if (placeLayerRef.current) placeLayerRef.current.remove();
    if (showPlaces) {
      const usedIds = new Set();
      for (const route of visibleRoutes) {
        for (const id of route.properties?.nearbyPlaceIds || []) usedIds.add(String(id));
      }
      const visiblePlaces = places.features.filter((place) => usedIds.has(String(place.id)));
      placeLayerRef.current = L.geoJSON(
        { type: "FeatureCollection", features: visiblePlaces },
        {
          pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
            radius: 4.5,
            color: "#26221c",
            weight: 1,
            fillColor: "#e8d8ad",
            fillOpacity: 0.95,
          }),
          onEachFeature: (feature, layer) => {
            const p = feature.properties || {};
            const period = [formatYear(p.startYear), formatYear(p.endYear)].filter(Boolean).join(" – ");
            layer.bindTooltip(p.name || "Ancient place", { direction: "top", offset: [0, -4] });
            layer.bindPopup(`
              <article class="rr-popup rr-place-popup">
                <div class="rr-popup-kicker">Pleiades place</div>
                <h3>${escapeHtml(p.name || "Ancient place")}</h3>
                ${p.type ? `<p><strong>Type:</strong> ${escapeHtml(p.type)}</p>` : ""}
                ${period ? `<p><strong>Recorded span:</strong> ${escapeHtml(period)}</p>` : ""}
                ${p.url ? `<p><a href="${escapeHtml(p.url)}" target="_blank" rel="noreferrer">Open Pleiades record ↗</a></p>` : ""}
              </article>`);
          },
        }
      ).addTo(map);
    }
  }, [routes, places, visibleRoutes, placesById, showPlaces]);

  const updateType = (key) => setTypes((old) => ({ ...old, [key]: !old[key] }));
  const updateCertainty = (key) => setCertainties((old) => ({ ...old, [key]: !old[key] }));

  return (
    <main className="roman-roads-page">
      <header className="rr-hero">
        <p className="rr-eyebrow">Roman Storyworld · Interactive Map</p>
        <h1>The Roman Roads Explorer</h1>
        <p className="rr-lead">Follow the network. Examine the evidence.</p>
        <p className="rr-intro">
          Roman roads can look deceptively certain on a modern map. This explorer keeps the source evidence visible,
          separating reconstructed routes by their published certainty and allowing the road network to be read as an
          archaeological argument rather than a finished diagram.
        </p>
      </header>

      <section className="rr-explorer" aria-label="Interactive Roman roads map of Britannia">
        <aside className="rr-controls">
          <div className="rr-control-section">
            <label className="rr-search-label" htmlFor="rr-route-search">Find a route or source</label>
            <input
              id="rr-route-search"
              className="rr-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Try Deva, Lewis, Margary…"
            />
          </div>

          <div className="rr-control-section">
            <h2>Network</h2>
            {Object.keys(TYPE_LABELS).map((key) => (
              <label className="rr-check" key={key}>
                <input type="checkbox" checked={Boolean(types[key])} onChange={() => updateType(key)} />
                <span className={`rr-swatch rr-swatch-${key.toLowerCase().replaceAll(" ", "-")}`} />
                <span>{TYPE_LABELS[key]}</span>
              </label>
            ))}
            <label className="rr-check rr-place-check">
              <input type="checkbox" checked={showPlaces} onChange={() => setShowPlaces((v) => !v)} />
              <span className="rr-place-dot" />
              <span>Ancient places <small>(Pleiades)</small></span>
            </label>
            <p className="rr-layer-note">
              <strong>Separate layer:</strong> these forts, settlements and stations are place records, not road-certainty
              ratings. A fort can therefore remain visible while only Hypothetical roads are selected.
            </p>
          </div>

          <div className="rr-control-section">
            <h2>Route evidence</h2>
            <div className="rr-evidence-explainer">
              <strong>Important:</strong> this describes confidence in the <em>mapped course</em> of a route — not whether
              a recognised Roman road existed. A well-known road may still be classed as Conjectured where its exact line
              cannot be plotted to Itiner-e's highest spatial certainty.
            </div>

            <label className={`rr-check ${routes && certaintyCounts.Certain === 0 ? "rr-check-disabled" : ""}`}>
              <input
                type="checkbox"
                checked={Boolean(certainties.Certain)}
                onChange={() => updateCertainty("Certain")}
                disabled={Boolean(routes && certaintyCounts.Certain === 0)}
              />
              <span className="rr-evidence rr-evidence-certain" />
              <span>
                Certain <strong className="rr-count">({certaintyCounts.Certain.toLocaleString()})</strong>
                {routes && certaintyCounts.Certain === 0 && <small className="rr-inline-note">none in this Britannia extract</small>}
              </span>
            </label>

            <label className="rr-check">
              <input type="checkbox" checked={Boolean(certainties.Conjectured)} onChange={() => updateCertainty("Conjectured")} />
              <span className="rr-evidence rr-evidence-conjectured" />
              <span>Conjectured <strong className="rr-count">({certaintyCounts.Conjectured.toLocaleString()})</strong></span>
            </label>

            <label className="rr-check">
              <input type="checkbox" checked={Boolean(certainties.Hypothetical)} onChange={() => updateCertainty("Hypothetical")} />
              <span className="rr-evidence rr-evidence-hypothetical" />
              <span>Hypothetical <strong className="rr-count">({certaintyCounts.Hypothetical.toLocaleString()})</strong></span>
            </label>

            <p className="rr-small-note">
              <strong>Conjectured:</strong> supported route, but the precise plotted alignment is less secure.
              <br />
              <strong>Hypothetical:</strong> proposed connection for which the route evidence is presently insufficient.
            </p>
          </div>

          <div className="rr-map-actions">
            <button type="button" onClick={() => mapRef.current?.fitBounds(BRITANNIA_BOUNDS, { padding: [12, 12] })}>Britannia</button>
            <button type="button" onClick={() => mapRef.current?.setView(DEVA, 9)}>Deva</button>
          </div>

          <div className="rr-result-count" aria-live="polite">
            {routes ? `${visibleRoutes.length.toLocaleString()} route segments visible` : "Loading route data…"}
          </div>
        </aside>

        <div className="rr-map-wrap">
          {loadingError && <div className="rr-error">Could not load the route data: {loadingError}</div>}
          <div ref={mapNode} className="rr-map" />
          <div className="rr-map-caption">
            <strong>How to read this map:</strong> road class and route certainty are different things. Pleiades place markers
            are independent of the Conjectured/Hypothetical road filters. Tap a road to inspect its evidence and sources.
          </div>
        </div>
      </section>

      <section className="rr-companion" aria-label="Companion Roman atlas">
        <div>
          <p className="rr-eyebrow">Use alongside the existing atlas</p>
          <h2>Places and roads answer different questions</h2>
          <p>
            Use DARE when you want to explore the places and geography of the Roman world. Use this explorer when you
            want to follow the network between them and inspect how securely each route has been reconstructed.
          </p>
        </div>
        <a href="https://imperium.ahlfeldt.se/" target="_blank" rel="noreferrer">Open the Digital Atlas of the Roman Empire ↗</a>
      </section>

      <section className="rr-method">
        <div>
          <p className="rr-eyebrow">How to read this map</p>
          <h2>A road line is a claim</h2>
        </div>
        <div className="rr-method-grid">
          <article>
            <span className="rr-method-line rr-method-conjectured" />
            <h3>Conjectured</h3>
            <p>The route is supported by evidence, but its precise course is reconstructed or spatially uncertain.</p>
          </article>
          <article>
            <span className="rr-method-line rr-method-hypothetical" />
            <h3>Hypothetical</h3>
            <p>A proposed connection where the evidence is currently insufficient to establish the route securely.</p>
          </article>
          <article>
            <span className="rr-method-line rr-method-storyworld" />
            <h3>Storyworld layer</h3>
            <p>Our investigations can sit above the source network without altering it: evidence, interpretation and storytelling remain visibly separate.</p>
          </article>
        </div>
      </section>

      <footer className="rr-sources">
        <p><strong>Road data:</strong> adapted from <a href="https://itiner-e.org/" target="_blank" rel="noreferrer">Itiner-e: the digital atlas of ancient roads</a>, CC BY 4.0. This Roman Storyworld extract is geographically filtered and restyled.</p>
        <p><strong>Ancient places:</strong> associated Pleiades records supplied through the Itiner-e export; Pleiades data is CC BY 3.0. Base mapping © OpenStreetMap contributors.</p>
        <p className="rr-data-note">Local Storyworld dataset prepared from the Itiner-e export supplied 29 August 2026. Source classifications and bibliographic fields are preserved in the pop-ups.</p>
      </footer>
    </main>
  );
}
